// src/features/legacy-reports/components/LegacyRangeControl.tsx

import { useEffect, useState } from 'react';
import { Group, Select, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { DateTime } from 'luxon';
import { SECONDS_PER_DAY, type UtcRange } from '@/shared/utils/utcRange';
import { LEGACY_REPORTS_QUERY_BOUNDARY_DATE } from '@/lib/constants/legacyReports';

/** Legacy parity presets (TimerangeUtil): UTC-day arithmetic via shared utcRange. */
export const LEGACY_RANGE_PRESETS = [
  'today',
  'yesterday',
  'last7d',
  'last30d',
  'currentMonth',
  'lastMonth',
  'currentYear',
  'custom',
] as const;
export type LegacyRangePreset = typeof LEGACY_RANGE_PRESETS[number];

const PRESET_LABELS: Record<LegacyRangePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7d: 'Last 7 Days',
  last30d: 'Last 30 Days',
  currentMonth: 'Current Month',
  lastMonth: 'Last Month',
  currentYear: 'Current Year',
  custom: 'Custom',
};

interface LegacyRangeControlProps {
  preset: LegacyRangePreset;
  onPresetChange: (preset: LegacyRangePreset) => void;
  customRange: UtcRange | null;
  onCustomRangeChange: (range: UtcRange) => void;
}

function toUtcDateString(seconds: number): string {
  return DateTime.fromSeconds(seconds, { zone: 'utc' }).toFormat('yyyy-MM-dd');
}

function committedValue(
  preset: LegacyRangePreset,
  customRange: UtcRange | null
): [string | null, string | null] {
  return preset === 'custom' && customRange
    ? [toUtcDateString(customRange.from), toUtcDateString(customRange.to - SECONDS_PER_DAY)]
    : [null, null];
}

export function LegacyRangeControl({
  preset,
  onPresetChange,
  customRange,
  onCustomRangeChange,
}: LegacyRangeControlProps) {
  // Hold the in-progress selection locally so the first click survives. A fully
  // controlled range picker whose parent stores only COMPLETE ranges feeds the
  // partial `[start, null]` back as `[null, null]`; Mantine then resets the pick,
  // making the range impossible to complete. Commit to the parent only when both
  // ends are set, and resync the draft when the range changes externally.
  const [pending, setPending] = useState<[string | null, string | null]>(() =>
    committedValue(preset, customRange)
  );

  useEffect(() => {
    setPending(committedValue(preset, customRange));
  }, [preset, customRange]);

  const handleCustomChange = (value: [string | null, string | null]) => {
    setPending(value);
    const [start, end] = value;
    if (!start || !end) return;
    const from = DateTime.fromFormat(start, 'yyyy-MM-dd', { zone: 'utc' });
    const lastDay = DateTime.fromFormat(end, 'yyyy-MM-dd', { zone: 'utc' });
    if (!from.isValid || !lastDay.isValid) return;
    onCustomRangeChange({
      from: from.toSeconds(),
      to: lastDay.plus({ days: 1 }).toSeconds(),
    });
  };

  return (
    <Group gap="sm" align="center">
      <Select
        data={LEGACY_RANGE_PRESETS.map((p) => ({ value: p, label: PRESET_LABELS[p] }))}
        value={preset}
        onChange={(value) => value && onPresetChange(value as LegacyRangePreset)}
        allowDeselect={false}
        w={160}
        size="sm"
        aria-label="Date range preset"
      />
      {preset === 'custom' && (
        <DatePickerInput
          type="range"
          allowSingleDateInRange
          value={pending}
          onChange={handleCustomChange}
          maxDate={LEGACY_REPORTS_QUERY_BOUNDARY_DATE}
          size="sm"
          w={260}
          aria-label="Custom date range (UTC)"
        />
      )}
      <Text size="xs" c="dimmed">
        Dates are UTC
      </Text>
    </Group>
  );
}
